import React, { useMemo, useRef, useEffect, useState } from 'react';
import './SMRTTimeCheck.css';
import { HoursMode, NetVarianceRow, ActiveFilter, OverrideData, VarianceUpdate, applyFilters } from './types';
import { fetchVariance, saveVarianceUpdates } from './api';
import FilterPanel from './FilterPanel';
import FilterChips from './FilterChips';
import ShiftsTable from './ShiftsTable';
import NetHoursTable from './NetHoursTable';

type Tab = 'shifts' | 'netHours';

interface AppliedState {
  filters: ActiveFilter[];
  startDate: string;
  endDate: string;
}

interface FetchKey {
  startDate: string;
  endDate: string;
}

function FunnelIcon() {
  return (
    <svg
      width="13" height="13"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
      style={{ display: 'block', flexShrink: 0 }}
    >
      <path d="M2 2h12l-4.5 5.5V14l-3-1.5V7.5L2 2z" />
    </svg>
  );
}

interface ActionsDropdownProps {
  onSave: () => void;
  onExport: () => void;
  onSubmit: () => void;
  saving: boolean;
  dirtyCount: number;
}

function ActionsDropdown({ onSave, onExport: _onExport, onSubmit: _onSubmit, saving, dirtyCount }: ActionsDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const saveLabel = saving
    ? 'Saving…'
    : dirtyCount > 0
      ? `Save (${dirtyCount} changed)`
      : 'Save';

  return (
    <div className="tc-actions-wrapper" ref={ref}>
      <button className="tc-btn-secondary" onClick={() => setOpen(o => !o)}>
        Actions ▼
      </button>
      {open && (
        <div className="tc-actions-menu">
          <button
            className="tc-actions-item"
            onClick={() => { onSave(); setOpen(false); }}
            disabled={dirtyCount === 0 || saving}
          >
            {saveLabel}
          </button>
          <button className="tc-actions-item" onClick={() => setOpen(false)}>Export</button>
          <button className="tc-actions-item" onClick={() => setOpen(false)}>Submit to Quickbooks</button>
        </div>
      )}
    </div>
  );
}

export default function SMRTTimeCheck() {
  const [activeTab, setActiveTab] = useState<Tab>('shifts');
  const [filterOpen, setFilterOpen] = useState(false);

  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [applied, setApplied] = useState<AppliedState | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);

  const [fetchedRows, setFetchedRows] = useState<NetVarianceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const lastFetchKey = useRef<FetchKey | null>(null);

  const d = new Date();
  const YESTERDAY = new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1)
    .toLocaleDateString('en-CA');

  const [overrides, setOverrides] = useState<Record<string, OverrideData>>({});
  const [modes, setModes] = useState<Record<string, HoursMode>>({});

  const effectiveRows: NetVarianceRow[] = useMemo(() =>
    fetchedRows.map(row => {
      const override = overrides[row.id];
      if (override) {
        return { ...row, hours_mode: HoursMode.OVERRIDE, override: override.hours, reason: override.reason, overridden_by: null };
      }
      const mode = modes[row.id];
      return mode ? { ...row, hours_mode: mode, override: null, reason: null } : row;
    }),
    [fetchedRows, overrides, modes],
  );

  // Rows whose hours_mode / override / reason differ from what was last fetched.
  const dirtyRows: NetVarianceRow[] = useMemo(() => {
    const fetchedById = new Map(fetchedRows.map(r => [r.id, r]));
    return effectiveRows.filter(row => {
      const orig = fetchedById.get(row.id);
      return orig && (
        row.hours_mode !== orig.hours_mode ||
        row.override   !== orig.override   ||
        row.reason     !== orig.reason
      );
    });
  }, [effectiveRows, fetchedRows]);

  const displayRows: NetVarianceRow[] = useMemo(() => {
    if (!applied) return [];
    return applyFilters(effectiveRows, applied.filters);
  }, [effectiveRows, applied]);

  const handleRefresh = async () => {
    if (!startDate) { setDateError('Start date is required.'); return; }
    if (!endDate)   { setDateError('End date is required.'); return; }
    if (endDate < startDate) { setDateError('End date must be on or after start date.'); return; }
    if (endDate > YESTERDAY) { setDateError('End date must be before today — data for the current day is incomplete.'); return; }
    setDateError(null);

    const key = lastFetchKey.current;
    const sameRange = key !== null && key.startDate === startDate && key.endDate === endDate;

    if (sameRange) {
      setApplied({ filters: activeFilters, startDate, endDate });
      return;
    }

    setLoading(true);
    setFetchError(null);
    try {
      const rows = await fetchVariance(startDate, endDate);
      setFetchedRows(rows);
      setOverrides({});
      setModes({});
      lastFetchKey.current = { startDate, endDate };
      setApplied({ filters: activeFilters, startDate, endDate });
    } catch (err: unknown) {
      setFetchError((err as Error).message ?? 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (dirtyRows.length === 0 || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updates: VarianceUpdate[] = dirtyRows.map(r => ({
        id: r.id,
        hours_mode: r.hours_mode,
        override: r.override,
        reason: r.reason,
      }));
      await saveVarianceUpdates(updates);

      // Promote saved values into fetchedRows so they become the new baseline
      const savedById = new Map(dirtyRows.map(r => [r.id, r]));
      setFetchedRows(prev => prev.map(row => {
        const saved = savedById.get(row.id);
        if (!saved) return row;
        // overridden_by is written server-side from the JWT — local copy is stale until next Refresh
        return { ...row, hours_mode: saved.hours_mode, override: saved.override, reason: saved.reason, overridden_by: null };
      }));
      // Clear local state for saved rows — fetchedRows now reflects the truth
      setOverrides(prev => {
        const next = { ...prev };
        dirtyRows.forEach(r => delete next[r.id]);
        return next;
      });
      setModes(prev => {
        const next = { ...prev };
        dirtyRows.forEach(r => delete next[r.id]);
        return next;
      });
    } catch (err: unknown) {
      setSaveError((err as Error).message ?? 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    alert('Exporting (TBD)');
  }

  const handleQBSubmit = () => {
    alert('Submitting to Quickbooks (TBD)');
  }


  const handleAddFilter = (f: ActiveFilter) => setActiveFilters(prev => [...prev, f]);
  const handleRemoveFilter = (id: string) => setActiveFilters(prev => prev.filter(f => f.id !== id));
  const handleSetOverride = (rowId: string, data: OverrideData) => setOverrides(prev => ({ ...prev, [rowId]: data }));
  const handleClearOverride = (rowId: string) => {
    setOverrides(prev => { const next = { ...prev }; delete next[rowId]; return next; });
    setModes(prev => ({ ...prev, [rowId]: HoursMode.WORKED }));
  };
  const handleSetMode = (rowId: string, mode: HoursMode) => setModes(prev => ({ ...prev, [rowId]: mode }));

  return (
    <div className="tc-page">
      <h1 className="tc-title">Smart Time Check</h1>

      <div className="tc-control-row">
        <button
          className={`tc-btn-secondary tc-filter-toggle${filterOpen ? ' tc-filter-toggle--active' : ''}`}
          onClick={() => setFilterOpen(o => !o)}
        >
          <FunnelIcon />
          Filter
          {activeFilters.length > 0 && (
            <span className="tc-filter-badge">{activeFilters.length}</span>
          )}
        </button>
        <ActionsDropdown onSave={handleSave} onExport={handleExport} onSubmit={handleQBSubmit} saving={saving} dirtyCount={dirtyRows.length} />
      </div>

      <FilterPanel isOpen={filterOpen} onAddFilter={handleAddFilter} />

      <div className="tc-date-row">
        <div className="tc-date-field">
          <label className="tc-date-label">Start</label>
          <input
            className="tc-date-input"
            type="date"
            value={startDate}
            max={endDate || YESTERDAY}
            onChange={e => setStartDate(e.target.value)}
          />
        </div>
        <div className="tc-date-field">
          <label className="tc-date-label">End</label>
          <input
            className="tc-date-input"
            type="date"
            value={endDate}
            min={startDate}
            max={YESTERDAY}
            onChange={e => setEndDate(e.target.value)}
          />
        </div>
      </div>

      {dateError  && <p className="tc-date-error">{dateError}</p>}
      {fetchError && <p className="tc-date-error">{fetchError}</p>}
      {saveError  && <p className="tc-date-error">{saveError}</p>}

      <FilterChips filters={activeFilters} onRemove={handleRemoveFilter} />

      <div className="tc-tabs">
        <button
          className={`tc-tab${activeTab === 'shifts' ? ' tc-tab--active' : ''}`}
          onClick={() => setActiveTab('shifts')}
        >
          Shifts
        </button>
        <button
          className={`tc-tab${activeTab === 'netHours' ? ' tc-tab--active' : ''}`}
          onClick={() => setActiveTab('netHours')}
        >
          Net Hours
        </button>
      </div>

      {activeTab === 'shifts' ? (
        <ShiftsTable
          rows={displayRows}
          loading={loading}
          onSetOverride={handleSetOverride}
          onClearOverride={handleClearOverride}
          onSetMode={handleSetMode}
          onRefresh={handleRefresh}
        />
      ) : (
        <NetHoursTable rows={displayRows} loading={loading} onRefresh={handleRefresh} />
      )}
    </div>
  );
}
